import { useEffect, useMemo, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api from "@/lib/api";
import { PageHeader, StatCard, EmptyState } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wallet, TrendingUp, Receipt, Download, FileText } from "lucide-react";

const monthName = (ym) => new Date(ym + "-01T00:00").toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
const monthShort = (ym) => new Date(ym + "-01T00:00").toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });

export default function Payments() {
  const [items, setItems] = useState(null);
  const [totalAll, setTotalAll] = useState(0);
  const [period, setPeriod] = useState("all");

  useEffect(() => {
    api.get("/therapist/payments").then(({ data }) => { setItems(data.items); setTotalAll(data.total); });
  }, []);

  const months = useMemo(() => {
    if (!items) return [];
    return [...new Set(items.map((a) => (a.date || "").slice(0, 7)).filter(Boolean))].sort().reverse();
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    return period === "all" ? items : items.filter((a) => (a.date || "").startsWith(period));
  }, [items, period]);

  const periodTotal = filtered.reduce((s, a) => s + (a.value || 0), 0);

  const chartData = useMemo(() => {
    if (!items) return [];
    const map = {};
    items.forEach((a) => { const m = (a.date || "").slice(0, 7); if (m) map[m] = (map[m] || 0) + (a.value || 0); });
    return Object.keys(map).sort().slice(-12).map((m) => ({ month: monthShort(m), total: map[m] }));
  }, [items]);

  const periodLabel = period === "all" ? "todos os períodos" : monthName(period);

  const exportCSV = () => {
    const rows = [["Cliente", "Serviço", "Data", "Horário", "Valor (R$)"],
      ...filtered.map((a) => [a.client_name, a.type, a.date, a.time, (a.value || 0).toFixed(2)])];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `teraviva-pagamentos-${period}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    // logo (brand mark) desenhado vetorialmente
    doc.setFillColor(79, 70, 229);
    doc.roundedRect(14, 12, 12, 12, 3, 3, "F");
    doc.setTextColor(79, 70, 229);
    doc.setFont("helvetica", "bold"); doc.setFontSize(18);
    doc.text("Teraviva", 30, 19);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(120, 120, 120);
    doc.text("Terapia + vida, evolução", 30, 24);
    doc.setTextColor(15, 23, 42); doc.setFontSize(14); doc.setFont("helvetica", "bold");
    doc.text("Relatório de Recebíveis", 14, 38);
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(80, 80, 80);
    doc.text(`Período: ${periodLabel}`, 14, 45);
    doc.text(`Total recebido no período: R$ ${periodTotal.toFixed(2)}  ·  Consultas: ${filtered.length}`, 14, 51);
    doc.text(`Emitido em: ${new Date().toLocaleDateString("pt-BR")}`, 14, 57);
    autoTable(doc, {
      startY: 63,
      head: [["Cliente", "Serviço", "Data", "Horário", "Valor (R$)"]],
      body: filtered.map((a) => [a.client_name, a.type, new Date(a.date + "T00:00").toLocaleDateString("pt-BR"), a.time, (a.value || 0).toFixed(2)]),
      headStyles: { fillColor: [79, 70, 229] },
      styles: { fontSize: 9 },
      foot: [["", "", "", "Total", `R$ ${periodTotal.toFixed(2)}`]],
      footStyles: { fillColor: [236, 253, 245], textColor: [16, 185, 129], fontStyle: "bold" },
    });
    doc.save(`teraviva-recebiveis-${period}.pdf`);
  };

  return (
    <div>
      <PageHeader title="Histórico de Pagamentos" subtitle="Consultas pagas e receita recebida"
        action={
          <div className="flex gap-2 flex-wrap">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-44" data-testid="period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os períodos</SelectItem>
                {months.map((m) => <SelectItem key={m} value={m} className="capitalize">{monthName(m)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" className="rounded-full" onClick={exportCSV} disabled={filtered.length === 0} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1" />CSV
            </Button>
            <Button className="rounded-full" onClick={exportPDF} disabled={filtered.length === 0} data-testid="export-pdf-btn">
              <FileText className="w-4 h-4 mr-1" />PDF
            </Button>
          </div>
        } />
      {!items ? <div className="h-40 animate-pulse bg-secondary rounded-2xl" /> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <StatCard icon={TrendingUp} label={`Recebido — ${periodLabel}`} value={`R$ ${periodTotal.toFixed(2)}`} accent="accent" />
            <StatCard icon={Wallet} label="Total recebido (geral)" value={`R$ ${totalAll.toFixed(2)}`} />
            <StatCard icon={Receipt} label="Consultas no período" value={filtered.length} accent="slate" />
          </div>

          {chartData.length > 0 && (
            <Card className="p-6 mb-6" data-testid="revenue-chart">
              <h3 className="font-heading font-medium mb-4">Receita por mês</h3>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(214 32% 91%)" />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} fontSize={12} />
                    <YAxis tickLine={false} axisLine={false} fontSize={12} tickFormatter={(v) => `R$${v}`} width={60} />
                    <Tooltip formatter={(v) => [`R$ ${Number(v).toFixed(2)}`, "Receita"]} cursor={{ fill: "hsl(210 40% 96%)" }} />
                    <Bar dataKey="total" fill="hsl(243 75% 59%)" radius={[6, 6, 0, 0]} maxBarSize={48} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          {filtered.length === 0 ? (
            <EmptyState icon={Receipt} title="Nenhum pagamento neste período" desc="Escolha outro período ou aguarde novos pagamentos." />
          ) : (
            <Card className="overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead><TableHead>Serviço</TableHead>
                    <TableHead>Data</TableHead><TableHead className="text-right">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((a) => (
                    <TableRow key={a.id} data-testid={`payment-row-${a.id}`}>
                      <TableCell className="font-medium">{a.client_name}</TableCell>
                      <TableCell className="text-muted-foreground">{a.type}</TableCell>
                      <TableCell className="text-muted-foreground">{new Date(a.date + "T00:00").toLocaleDateString("pt-BR")} · {a.time}</TableCell>
                      <TableCell className="text-right font-medium text-accent">R$ {(a.value || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
